import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import api, { type DispatchTripBundle, type Order, type OrderStatusEvent, type UnitMove } from '../lib/api'
import LifecycleBoard from '../components/LifecycleBoard'
import UnifiedTimeline from '../components/UnifiedTimeline'
import { SupportReadOnlyNotice, useSupportReadOnlyView } from '../lib/support-session'

const ISSUE_STATUS_OPTIONS = [
  'NEW',
  'TRIAGE',
  'NEEDS_MORE_EVIDENCE',
  'PRE_EXISTING',
  'RESOLVED_ON_SITE',
  'NOT_DAMAGE',
  'ESCALATE',
  'CUSTOMER_NOTIFICATION_APPROVED',
  'CLAIM_OPEN',
  'CLOSED',
]

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PLANNING: 'bg-indigo-100 text-indigo-800',
  READY: 'bg-green-100 text-green-800',
  POSTED_AS_LOAD: 'bg-blue-100 text-blue-800',
  DIRECT_ASSIGNED: 'bg-purple-100 text-purple-800',
  CLOSED: 'bg-slate-200 text-slate-700',
}

function fmtMoney(cents?: number | null) {
  if (typeof cents !== 'number') return '—'
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function placeSummary(snapshot: any): string {
  if (!snapshot) return '—'
  const city = snapshot.city ?? ''
  const state = snapshot.state ?? ''
  const zip = snapshot.zip ?? ''
  const address = snapshot.address1 ?? ''
  const cityState = [city, state].filter(Boolean).join(', ')
  return [address, cityState, zip].filter(Boolean).join(' • ') || '—'
}

function lifecycleForOrder(
  order: Order,
  timeline: OrderStatusEvent[],
  canonicalTripJobId?: string | null,
  hasDispatchTrip = false,
) {
  const missing: string[] = []
  const blocked: string[] = []
  const assignedJobId = canonicalTripJobId ?? order.assignedJobId

  if (!order.deliveryContactName) missing.push('Missing delivery contact.')
  if (!order.deliveryContactPhone) missing.push('Missing delivery contact phone.')
  if (!order.deliveryWindowStart || !order.deliveryWindowEnd) missing.push('Missing delivery appointment window.')
  if (!order.pickupContactName) missing.push('Missing pickup contact.')

  if (order.customerChangeRequestState === 'REVIEW_REQUIRED') {
    blocked.push(`Customer change review required: ${order.customerChangeRequestReason ?? 'Review requested.'}`)
  }

  let currentStatus = 'Quote Accepted'
  let owner = 'Dispatcher'
  let nextBestAction = 'Send completion link'
  let nextActionTo: string | undefined = '/quote-desk'

  if (hasDispatchTrip || assignedJobId) {
    currentStatus = 'Active Trip'
    owner = 'Driver Ops'
    nextBestAction = assignedJobId ? 'Open driver trip' : 'Open active trip'
    nextActionTo = assignedJobId
      ? '/jobs/' + assignedJobId
      : (order.postedLoadId ? '/loads/' + order.postedLoadId : undefined)
  } else if (order.status === 'READY') {
    currentStatus = 'Ready for Dispatch'
    owner = 'Dispatch'
    nextBestAction = 'Create Trip'
    nextActionTo = '/loads/new'
  } else if (order.customerChangeRequestState === 'REVIEW_REQUIRED') {
    currentStatus = 'Dispatch Review'
    owner = 'Dispatcher'
    nextBestAction = 'Review and approve customer changes'
    nextActionTo = undefined
  } else if (order.customerSubmissionState === 'SUBMITTED') {
    currentStatus = 'Customer Submitted'
    owner = 'Dispatcher'
    nextBestAction = missing.length ? 'Resolve missing completion fields' : 'Set order to READY'
    nextActionTo = undefined
  } else if (order.customerCompletionState === 'CUSTOMER_INFO_IN_PROGRESS' || order.customerCompletionState === 'CUSTOMER_SUBMITTED') {
    currentStatus = 'Customer Completing Details'
    owner = 'Customer'
    nextBestAction = 'Send Completion Link'
    nextActionTo = '/quote-desk'
  }

  const lifecycleTimeline = [
    { label: 'Quote Accepted', at: order.createdAt },
    { label: 'Customer Completing Details', at: order.updatedAt },
    ...(order.customerSubmittedAt ? [{ label: 'Customer Submitted', at: order.customerSubmittedAt }] : []),
    ...(order.customerChangeRequestState === 'REVIEW_REQUIRED' ? [{ label: 'Dispatch Review', at: order.updatedAt }] : []),
    ...(order.status === 'READY' ? [{ label: 'Ready for Dispatch', at: order.updatedAt }] : []),
    ...timeline.slice(0, 5).map((event) => ({ label: `Order ${event.status}`, at: event.created_at })),
  ]

  return { currentStatus, owner, missing, blocked, nextBestAction, nextActionTo, lifecycleTimeline }
}

function assignmentLifecycleForOrder(order: Order, canonicalTripJobId?: string | null, hasDispatchTrip = false) {
  const missing: string[] = []
  const blocked: string[] = []

  let currentStatus = 'Ready for Dispatch'
  let owner = 'Dispatcher'
  let nextBestAction = 'Create Trip'
  let nextActionTo: string | undefined = '/loads/new'
  const assignedJobId = canonicalTripJobId ?? order.assignedJobId

  if (order.status !== 'READY') {
    currentStatus = 'Awaiting Ready Status'
    owner = 'Dispatcher'
    blocked.push('Order must reach READY before trip assignment.')
    nextBestAction = 'Complete dispatch review'
    nextActionTo = undefined
  } else if (!order.postedLoadId && !assignedJobId && !hasDispatchTrip) {
    missing.push('Trip not created.')
    missing.push('Carrier not assigned.')
    missing.push('Driver not assigned.')
    missing.push('Equipment not assigned.')
  } else if (order.postedLoadId && !assignedJobId && !hasDispatchTrip) {
    currentStatus = 'Trip Created'
    owner = 'Dispatcher'
    missing.push('Carrier assignment pending.')
    missing.push('Driver assignment pending.')
    missing.push('Equipment assignment pending.')
    nextBestAction = 'Assign carrier and driver'
    nextActionTo = `/loads/${order.postedLoadId}`
  } else if (hasDispatchTrip || assignedJobId) {
    currentStatus = hasDispatchTrip ? 'Active Trip' : 'Assignment Complete'
    owner = 'Driver Ops'
    nextBestAction = assignedJobId ? 'Open driver trip' : 'Open active trip'
    nextActionTo = assignedJobId ? '/jobs/' + assignedJobId : (order.postedLoadId ? '/loads/' + order.postedLoadId : undefined)
  }

  if (order.customerChangeRequestState === 'REVIEW_REQUIRED') {
    blocked.push('Customer change review still open.')
  }

  const timeline = [
    { label: 'Ready for Dispatch', at: order.status === 'READY' ? order.updatedAt : undefined },
    ...(order.postedLoadId ? [{ label: 'Trip Created', at: order.updatedAt }] : []),
    ...(assignedJobId ? [{ label: 'Carrier / Driver / Equipment Assigned', at: order.updatedAt }] : []),
  ]

  return { currentStatus, owner, missing, blocked, nextBestAction, nextActionTo, timeline }
}

export default function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const { isSupportReadOnlyView } = useSupportReadOnlyView()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [order, setOrder] = useState<Order | null>(null)
  const [unitMoves, setUnitMoves] = useState<UnitMove[]>([])
  const [timeline, setTimeline] = useState<OrderStatusEvent[]>([])
  const [saving, setSaving] = useState(false)
  const [posting, setPosting] = useState(false)
  const [assigning, setAssigning] = useState(false)
  const [tripSaving, setTripSaving] = useState(false)
  const [dispatchingTrip, setDispatchingTrip] = useState(false)
  const [inspectionTimeline, setInspectionTimeline] = useState<Record<string, any[]>>({})
  const [inspectionMessage, setInspectionMessage] = useState<string | null>(null)
  const [inspectionIssues, setInspectionIssues] = useState<Record<string, any[]>>({})
  const [issueNotesById, setIssueNotesById] = useState<Record<string, string>>({})
  const [dispatchTripBundle, setDispatchTripBundle] = useState<DispatchTripBundle | null>(null)
  const [unifiedTimeline, setUnifiedTimeline] = useState<any[]>([])

  const [status, setStatus] = useState('DRAFT')
  const [notes, setNotes] = useState('')
  const [carrierOrgId, setCarrierOrgId] = useState('')
  const [driverUserId, setDriverUserId] = useState('')
  const [tripTrailerNumber, setTripTrailerNumber] = useState('')
  const [tripAssignmentNotes, setTripAssignmentNotes] = useState('')
  const canPostAsLoad = !order?.postedLoadId && !['DIRECT_ASSIGNED', 'CLOSED'].includes(status)
  const canAssignDirect = !order?.assignedJobId && !['DIRECT_ASSIGNED', 'DELIVERED', 'CANCELLED', 'CLOSED'].includes(status)

  const loadOrder = async () => {
    if (!orderId) return
    setLoading(true)
    setError(null)
    try {
      const response = await api.orders.get(orderId)
      setOrder(response.data.order)
      setUnitMoves(response.data.unitMoves)
      setTimeline(response.data.statusTimeline)
      setStatus(response.data.order.status)
      setNotes(response.data.order.notes ?? '')
      const tripResponse = await api.orders.getDispatchTrip(orderId)
      setDispatchTripBundle(tripResponse.data)
      const unifiedTimelineResponse = await api.orders.unifiedTimeline(orderId)
      setUnifiedTimeline(unifiedTimelineResponse.data.timeline ?? [])
      if (tripResponse.data?.trip) {
        const trip = tripResponse.data.trip
        setTripTrailerNumber(trip.trailerNumber ?? '')
        setTripAssignmentNotes(trip.assignmentNotes ?? '')
        if (!carrierOrgId && trip.carrierOrgId) setCarrierOrgId(trip.carrierOrgId)
        if (!driverUserId && trip.driverUserId) setDriverUserId(trip.driverUserId)
      }
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to load order')
    } finally {
      setLoading(false)
    }
  }

  const loadInspectionTimeline = async (units: UnitMove[]) => {
    const byUnit: Record<string, any[]> = {}
    await Promise.all(
      units.map(async (unit) => {
        try {
          const res = await api.vehicleInspections.timeline(unit.id)
          byUnit[unit.id] = res.data.inspections || []
        } catch {
          byUnit[unit.id] = []
        }
      })
    )
    setInspectionTimeline(byUnit)
  }

  const loadInspectionIssues = async (units: UnitMove[]) => {
    const byUnit: Record<string, any[]> = {}
    await Promise.all(
      units.map(async (unit) => {
        const entries = inspectionTimeline[unit.id] || []
        const latest = entries[0]
        if (!latest?.id) {
          byUnit[unit.id] = []
          return
        }
        try {
          const res = await api.vehicleInspections.issuesForInspection(latest.id)
          byUnit[unit.id] = res.data.issues || []
        } catch {
          byUnit[unit.id] = []
        }
      })
    )
    setInspectionIssues(byUnit)
  }

  useEffect(() => {
    void loadOrder()
  }, [orderId])

  useEffect(() => {
    if (!unitMoves.length) return
    void loadInspectionTimeline(unitMoves)
  }, [unitMoves.map((u) => u.id).join('|')])

  useEffect(() => {
    if (!unitMoves.length) return
    void loadInspectionIssues(unitMoves)
  }, [unitMoves.map((u) => u.id).join('|'), JSON.stringify(inspectionTimeline)])

  const issuePickupLink = async (unit: UnitMove) => {
    if (!orderId) return
    setInspectionMessage(null)
    setError(null)
    try {
      const res = await api.vehicleInspections.createPickup({
        orderId,
        unitId: unit.id,
      })
      setInspectionMessage(`Pickup link issued for ${unit.vin.slice(-6)}: ${res.data.publicLink}`)
      await loadInspectionTimeline(unitMoves)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to issue pickup inspection link')
    }
  }

  const issueDeliveryLink = async (unit: UnitMove) => {
    if (!orderId) return
    setInspectionMessage(null)
    setError(null)
    try {
      const res = await api.vehicleInspections.createDelivery({
        orderId,
        unitId: unit.id,
      })
      setInspectionMessage(`Delivery link issued for ${unit.vin.slice(-6)}: ${res.data.publicLink}`)
      await loadInspectionTimeline(unitMoves)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to issue delivery inspection link')
    }
  }

  const saveForPlanning = async () => {
    if (!orderId) return
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      await api.orders.update(orderId, { status, notes, statusNote: 'Saved for planning' })
      setNotice('Order updated.')
      await loadOrder()
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to save order')
    } finally {
      setSaving(false)
    }
  }

  const postAsLoad = async () => {
    if (!orderId) return
    setPosting(true)
    setError(null)
    setNotice(null)
    try {
      const response = await api.orders.postAsLoad(orderId)
      setNotice(response.data.posted ? `Order posted as load ${response.data.loadNumber ?? response.data.loadId}.` : response.data.message ?? 'Order already posted as load.')
      await loadOrder()
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to post as load')
    } finally {
      setPosting(false)
    }
  }

  const assignDirect = async () => {
    if (!orderId) return
    if (!carrierOrgId.trim()) {
      setError('Carrier Org ID is required for direct assignment.')
      return
    }
    setAssigning(true)
    setError(null)
    setNotice(null)
    try {
      const response = await api.orders.assignDirect(orderId, {
        carrierOrgId: carrierOrgId.trim(),
        driverUserId: driverUserId.trim() || undefined,
      })
      setNotice(`Order assigned directly to job ${response.data.jobNumber}.`)
      await loadOrder()
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to assign directly')
    } finally {
      setAssigning(false)
    }
  }

  const saveDispatchTrip = async () => {
    if (!orderId) return
    setTripSaving(true)
    setError(null)
    setNotice(null)
    try {
      const response = await api.orders.upsertDispatchTrip(orderId, {
        carrierOrgId: carrierOrgId.trim() || undefined,
        driverUserId: driverUserId.trim() || undefined,
        trailerNumber: tripTrailerNumber.trim() || undefined,
        assignmentNotes: tripAssignmentNotes.trim() || undefined,
      })
      setDispatchTripBundle(response.data)
      setNotice('Trip assignment saved.')
      await loadOrder()
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to save trip assignment')
    } finally {
      setTripSaving(false)
    }
  }

  const dispatchTripNow = async () => {
    if (!orderId) return
    setDispatchingTrip(true)
    setError(null)
    setNotice(null)
    try {
      const response = await api.orders.dispatchTrip(orderId)
      setDispatchTripBundle(response.data)
      setNotice(`Trip ${response.data.trip?.tripNumber ?? ''} dispatched.`.trim())
      await loadOrder()
    } catch (err: any) {
      const blockers = err?.response?.data?.blockers
      if (Array.isArray(blockers) && blockers.length) {
        setError(`Dispatch blocked: ${blockers.join(', ')}`)
      } else {
        setError(err?.response?.data?.error ?? 'Failed to dispatch trip')
      }
    } finally {
      setDispatchingTrip(false)
    }
  }

  const confirmStopAppointment = async (stopId: string) => {
    if (!orderId) return
    setError(null)
    try {
      const response = await api.orders.updateDispatchTripStop(orderId, stopId, {
        appointmentStatus: 'CONFIRMED',
        appointmentConfirmed: true,
      })
      setDispatchTripBundle(response.data)
      setNotice('Stop appointment confirmed.')
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to update stop appointment')
    }
  }

  const setStopAppointmentStatus = async (stopId: string, appointmentStatus: 'REQUESTED' | 'CONFIRMED' | 'RESCHEDULE_REQUESTED' | 'MISSED' | 'COMPLETED') => {
    if (!orderId) return
    setError(null)
    try {
      const response = await api.orders.updateDispatchTripStop(orderId, stopId, {
        appointmentStatus,
        appointmentConfirmed: appointmentStatus === 'CONFIRMED' || appointmentStatus === 'COMPLETED',
      })
      setDispatchTripBundle(response.data)
      setNotice(`Stop appointment set to ${appointmentStatus}.`)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to update stop appointment status')
    }
  }

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading order...</div>
  if (error && !order) return <div className="p-6 text-sm text-red-600">{error}</div>
  if (!order) return <div className="p-6 text-sm text-gray-500">Order not found.</div>

  const hasDispatchTrip = Boolean(dispatchTripBundle?.trip || order.activeDispatchTripId)
  const canonicalTripJobId = dispatchTripBundle?.trip?.jobId ?? order.assignedJobId
  const lifecycle = lifecycleForOrder(order, timeline, canonicalTripJobId, hasDispatchTrip)
  const assignmentLifecycle = assignmentLifecycleForOrder(order, canonicalTripJobId, hasDispatchTrip)

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">{order.orderNumber}</h1>
            <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[order.status] ?? 'bg-gray-100 text-gray-700'}`}>
              {order.status}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1">Customer: {order.customerName ?? '—'} • VINs: {unitMoves.length}</p>
        </div>
        <Link to="/orders" className="text-sm text-blue-700 hover:underline">← Back to Orders</Link>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}
      {notice && <div className="rounded border border-green-200 bg-green-50 text-green-700 px-3 py-2 text-sm">{notice}</div>}
      {inspectionMessage && <div className="rounded border border-blue-200 bg-blue-50 text-blue-700 px-3 py-2 text-sm">{inspectionMessage}</div>}
      <SupportReadOnlyNotice />

      <LifecycleBoard
        title="Quote to Dispatch Lifecycle"
        currentStatus={lifecycle.currentStatus}
        owner={lifecycle.owner}
        missing={lifecycle.missing}
        blocked={lifecycle.blocked}
        nextBestAction={lifecycle.nextBestAction}
        nextActionTo={lifecycle.nextActionTo}
        timeline={lifecycle.lifecycleTimeline}
      />

      <LifecycleBoard
        title="Trip / Carrier / Driver / Equipment Assignment"
        currentStatus={assignmentLifecycle.currentStatus}
        owner={assignmentLifecycle.owner}
        missing={assignmentLifecycle.missing}
        blocked={assignmentLifecycle.blocked}
        nextBestAction={assignmentLifecycle.nextBestAction}
        nextActionTo={assignmentLifecycle.nextActionTo}
        timeline={assignmentLifecycle.timeline}
      />

      <UnifiedTimeline title="Unified Timeline" entries={unifiedTimeline} defaultFilter="operations" />

      <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-gray-500">Customer</div>
          <div>{order.customerName ?? '—'}</div>
        </div>
        <div>
          <div className="text-gray-500">Account</div>
          <div>{order.customerAccount ?? '—'}</div>
        </div>
        <div>
          <div className="text-gray-500">Status</div>
          <fieldset disabled={isSupportReadOnlyView}>
            <select className="border rounded px-2 py-1 mt-1 w-full disabled:bg-gray-50 disabled:text-gray-400" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="DRAFT">DRAFT</option>
              <option value="PLANNING">PLANNING</option>
              <option value="READY">READY</option>
              <option value="POSTED_AS_LOAD">POSTED_AS_LOAD</option>
              <option value="DIRECT_ASSIGNED">DIRECT_ASSIGNED</option>
              <option value="CLOSED">CLOSED</option>
            </select>
          </fieldset>
        </div>
        <div>
          <div className="text-gray-500">Ready Date</div>
          <div>{order.readyDate ? new Date(order.readyDate).toLocaleDateString() : '—'}</div>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <h2 className="font-semibold mb-1">Pickup</h2>
          <div>{placeSummary(order.pickupSnapshot)}</div>
          <div className="text-gray-500 mt-1">Window: {order.pickupWindowStart ?? '—'} to {order.pickupWindowEnd ?? '—'}</div>
        </div>
        <div>
          <h2 className="font-semibold mb-1">Delivery</h2>
          <div>{placeSummary(order.deliverySnapshot)}</div>
          <div className="text-gray-500 mt-1">Window: {order.deliveryWindowStart ?? '—'} to {order.deliveryWindowEnd ?? '—'}</div>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-gray-500">Customer Price</div>
          <div>{fmtMoney(order.customerPriceCents)}</div>
        </div>
        <div>
          <div className="text-gray-500">Carrier Cost</div>
          <div>{fmtMoney(order.carrierCostCents)}</div>
        </div>
        <div>
          <div className="text-gray-500">Margin</div>
          <div>{fmtMoney(order.marginCents)}</div>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-4 space-y-3">
        <h2 className="font-semibold">VIN / Unit Moves</h2>
        {!unitMoves.length && <div className="text-sm text-gray-500">No unit moves found.</div>}
        {!!unitMoves.length && (
          <div className="space-y-2">
            {unitMoves.map((unitMove) => (
              <div key={unitMove.id} className="border rounded-lg p-3 text-sm grid grid-cols-1 md:grid-cols-7 gap-2">
                <div className="md:col-span-2">
                  <div className="font-medium">{unitMove.vin}</div>
                  <div className="text-xs text-gray-500">Last 6: {unitMove.vin.slice(-6)}</div>
                </div>
                <div>
                  {[unitMove.vehicle?.year, unitMove.vehicle?.make, unitMove.vehicle?.model, unitMove.vehicle?.bodyStyle]
                    .filter(Boolean)
                    .join(' ') || '—'}
                </div>
                <div>
                  Pickup: {placeSummary(unitMove.pickupPlaceSnapshot)}
                </div>
                <div>
                  Delivery: {placeSummary(unitMove.deliveryPlaceSnapshot)}
                </div>
                <div>Status: {unitMove.status}</div>
                <div>Ready: {unitMove.readyDate ? new Date(unitMove.readyDate).toLocaleDateString() : '—'}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border rounded-xl p-4 space-y-3">
        <h2 className="font-semibold">Unit Inspections</h2>
        {!unitMoves.length && <div className="text-sm text-gray-500">No units available for inspection.</div>}
        {!!unitMoves.length && (
          <div className="space-y-3">
            {unitMoves.map((unit) => {
              const entries = inspectionTimeline[unit.id] || []
              const pickup = entries.find((e: any) => e.inspection_type === 'PICKUP')
              const delivery = entries.find((e: any) => e.inspection_type === 'DELIVERY')
              return (
                <div key={unit.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-medium text-sm">VIN {unit.vin} • Unit {unit.id.slice(0, 8)}</div>
                      <div className="text-xs text-gray-500">Pickup: {pickup?.status || 'NOT_CREATED'} • Delivery: {delivery?.status || 'NOT_CREATED'}</div>
                    </div>
                    <div className="text-xs text-gray-500">Photos P/D: {pickup?.photo_count || 0}/{delivery?.photo_count || 0}</div>
                  </div>
                  <div className="text-xs text-gray-500">
                    Signatures P/D: {pickup?.signature_count || 0}/{delivery?.signature_count || 0} • Exceptions: {delivery?.exception_count || 0}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <fieldset disabled={isSupportReadOnlyView} className="flex flex-wrap gap-2">
                      <button onClick={() => void issuePickupLink(unit)} className="px-3 py-1 rounded border text-xs text-blue-700 hover:underline disabled:opacity-50">Issue Pickup Link</button>
                      <button onClick={() => void issueDeliveryLink(unit)} className="px-3 py-1 rounded border text-xs text-blue-700 hover:underline disabled:opacity-50">Issue Delivery Link</button>
                      {pickup?.id && (
                        <button
                          onClick={async () => {
                            try {
                              const re = await api.vehicleInspections.reissueLink(pickup.id)
                              setInspectionMessage(`Pickup link reissued for ${unit.vin.slice(-6)}: ${re.data.publicLink}`)
                            } catch (err: any) {
                              setError(err?.response?.data?.error ?? 'Failed to reissue pickup link')
                            }
                          }}
                          className="px-3 py-1 rounded border text-xs text-blue-700 hover:underline disabled:opacity-50"
                        >
                          Reissue Pickup Link
                        </button>
                      )}
                      {delivery?.id && (
                        <button
                          onClick={async () => {
                            try {
                              const re = await api.vehicleInspections.reissueLink(delivery.id)
                              setInspectionMessage(`Delivery link reissued for ${unit.vin.slice(-6)}: ${re.data.publicLink}`)
                            } catch (err: any) {
                              setError(err?.response?.data?.error ?? 'Failed to reissue delivery link')
                            }
                          }}
                          className="px-3 py-1 rounded border text-xs text-blue-700 hover:underline disabled:opacity-50"
                        >
                          Reissue Delivery Link
                        </button>
                      )}
                    </fieldset>
                  </div>
                  {!!entries.length && (
                    <div className="space-y-1 text-xs text-gray-600">
                      {entries.slice(0, 4).map((entry: any) => (
                        <div key={entry.id} className="border rounded px-2 py-1">
                          {entry.inspection_type} • {entry.status} • {entry.created_at ? new Date(entry.created_at).toLocaleString() : '—'}
                        </div>
                      ))}
                    </div>
                  )}
                  {!!inspectionIssues[unit.id]?.length && (
                    <div className="space-y-2 pt-2 border-t">
                      <div className="text-xs font-semibold text-gray-700">Condition Issues (Private Triage)</div>
                      {inspectionIssues[unit.id].map((issue: any) => (
                        <div key={issue.id} className="rounded border px-2 py-2 space-y-2 text-xs">
                          <div className="font-medium">{issue.reportedArea} • {issue.severity} • {issue.status}</div>
                          <div className="text-gray-600">{issue.description}</div>
                          <fieldset disabled={isSupportReadOnlyView} className="flex flex-wrap items-center gap-2">
                            <select
                              className="border rounded px-2 py-1 disabled:bg-gray-50 disabled:text-gray-400"
                              value={issue.status}
                              onChange={async (e) => {
                                try {
                                  const nextStatus = e.target.value
                                  const internalNotes = issueNotesById[issue.id] || undefined
                                  await api.vehicleInspections.updateIssueStatus(issue.id, { status: nextStatus, internalNotes })
                                  await loadInspectionIssues(unitMoves)
                                } catch (err: any) {
                                  setError(err?.response?.data?.error ?? 'Failed to update issue status')
                                }
                              }}
                            >
                              {ISSUE_STATUS_OPTIONS.map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                            <input
                              className="border rounded px-2 py-1 disabled:bg-gray-50 disabled:text-gray-400"
                              placeholder="Internal notes (optional)"
                              value={issueNotesById[issue.id] || ''}
                              onChange={(e) => setIssueNotesById((prev) => ({ ...prev, [issue.id]: e.target.value }))}
                            />
                            <button
                              className="px-2 py-1 rounded border text-blue-700 disabled:opacity-50"
                              onClick={async () => {
                                try {
                                  const note = issueNotesById[issue.id]
                                  if (!note) return
                                  await api.vehicleInspections.addIssueCarrierNote(issue.id, { note })
                                  setIssueNotesById((prev) => ({ ...prev, [issue.id]: '' }))
                                  await loadInspectionIssues(unitMoves)
                                } catch (err: any) {
                                  setError(err?.response?.data?.error ?? 'Failed to add issue note')
                                }
                              }}
                            >
                              Add Note
                            </button>
                          </fieldset>
                        </div>
                      ))}
                    </div>
                  )}
                  {!entries.length && <div className="text-xs text-gray-500">No inspection records yet for this unit.</div>}
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="bg-white border rounded-xl p-4">
        <h2 className="font-semibold mb-2">Status Timeline</h2>
        {!timeline.length && <div className="text-sm text-gray-500">No status events yet.</div>}
        {!!timeline.length && (
          <div className="space-y-2">
            {[...timeline]
              .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
              .map((event) => (
              <div key={event.id} className="border rounded px-3 py-2 text-sm">
                <div className="font-medium">{event.status}</div>
                <div className="text-gray-500">{event.note ?? '—'}</div>
                <div className="text-xs text-gray-400">{new Date(event.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border rounded-xl p-4 text-sm space-y-2">
        <h2 className="font-semibold">Related Links</h2>
        <div className="flex flex-wrap gap-2">
          {order.sourceQuoteId && (
            <Link to={`/quotes/${order.sourceQuoteId}`} className="px-3 py-1 rounded border text-blue-700 hover:underline">
              Source Quote
            </Link>
          )}
          {order.postedLoadId && (
            <Link to={`/loads/${order.postedLoadId}`} className="px-3 py-1 rounded border text-blue-700 hover:underline">
              Created Load
            </Link>
          )}
        </div>
      </div>

      <div className="bg-white border rounded-xl p-4 space-y-3 text-sm">
        <h2 className="font-semibold">Dispatch Trip</h2>
        {!dispatchTripBundle?.trip && <div className="text-gray-500">No dispatch trip created for this order yet.</div>}
        <div className="space-y-2">
          {dispatchTripBundle?.trip && (
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-1 rounded bg-blue-50 text-blue-800 border border-blue-200">{dispatchTripBundle.trip.tripNumber}</span>
              <span className="px-2 py-1 rounded bg-gray-50 text-gray-700 border border-gray-200">{dispatchTripBundle.trip.status}</span>
              {dispatchTripBundle.trip.dispatchAt && (
                <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Dispatched {new Date(dispatchTripBundle.trip.dispatchAt).toLocaleString()}
                </span>
              )}
            </div>
          )}

          {!!dispatchTripBundle?.trip?.blockers?.length && (
              <div className="rounded border border-amber-200 bg-amber-50 p-2 text-amber-800">
                <div className="font-medium">Dispatch blockers</div>
              <div>{dispatchTripBundle.trip?.blockers?.join(', ')}</div>
              </div>
            )}

            <fieldset disabled={isSupportReadOnlyView} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <input className="border rounded px-3 py-2 disabled:bg-gray-50 disabled:text-gray-400" value={carrierOrgId} onChange={(e) => setCarrierOrgId(e.target.value)} placeholder="Carrier Org ID" />
                <input className="border rounded px-3 py-2 disabled:bg-gray-50 disabled:text-gray-400" value={driverUserId} onChange={(e) => setDriverUserId(e.target.value)} placeholder="Driver User ID" />
                <input className="border rounded px-3 py-2 disabled:bg-gray-50 disabled:text-gray-400" value={tripTrailerNumber} onChange={(e) => setTripTrailerNumber(e.target.value)} placeholder="Trailer Number" />
              </div>
              <textarea className="w-full border rounded px-3 py-2 disabled:bg-gray-50 disabled:text-gray-400" rows={2} value={tripAssignmentNotes} onChange={(e) => setTripAssignmentNotes(e.target.value)} placeholder="Trip assignment notes" />

              <div className="flex flex-wrap gap-2">
                <button onClick={() => void saveDispatchTrip()} disabled={tripSaving} className="bg-indigo-700 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-800 disabled:opacity-50">
                  {tripSaving ? 'Saving Trip...' : 'Save Trip Assignment'}
                </button>
                <button onClick={() => void dispatchTripNow()} disabled={dispatchingTrip} className="bg-emerald-700 text-white px-4 py-2 rounded-lg font-semibold hover:bg-emerald-800 disabled:opacity-50">
                  {dispatchingTrip ? 'Dispatching...' : 'Dispatch Trip'}
                </button>
              </div>
            </fieldset>

          {!!dispatchTripBundle?.stops?.length && (
              <div className="space-y-2">
                <div className="font-medium">Stops</div>
              {dispatchTripBundle.stops.map((stop) => (
                  <div key={stop.id} className="border rounded p-2 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="font-medium">{stop.stopType}</div>
                      <div className="text-gray-500 text-xs">Appointment: {stop.appointmentStatus ?? 'NOT_REQUIRED'}</div>
                    </div>
                    <fieldset disabled={isSupportReadOnlyView} className="flex gap-2">
                      {stop.appointmentRequired && stop.appointmentStatus !== 'CONFIRMED' && (
                        <button onClick={() => void confirmStopAppointment(stop.id)} className="px-3 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
                          Confirm Appointment
                        </button>
                      )}
                      {stop.appointmentRequired && (
                        <>
                          <button onClick={() => void setStopAppointmentStatus(stop.id, 'REQUESTED')} className="px-3 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50">
                            Mark Requested
                          </button>
                          <button onClick={() => void setStopAppointmentStatus(stop.id, 'RESCHEDULE_REQUESTED')} className="px-3 py-1 rounded border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                            Request Reschedule
                          </button>
                          <button onClick={() => void setStopAppointmentStatus(stop.id, 'MISSED')} className="px-3 py-1 rounded border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50">
                            Mark Missed
                          </button>
                          <button onClick={() => void setStopAppointmentStatus(stop.id, 'COMPLETED')} className="px-3 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
                            Mark Completed
                          </button>
                        </>
                      )}
                    </fieldset>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>

      <div className="bg-white border rounded-xl p-4 space-y-3">
        <h2 className="font-semibold">Actions</h2>
        <fieldset disabled={isSupportReadOnlyView} className="space-y-3">
          <textarea className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Order notes" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input className="border rounded px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400" value={carrierOrgId} onChange={(e) => setCarrierOrgId(e.target.value)} placeholder="Carrier Org ID" />
            <input className="border rounded px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400" value={driverUserId} onChange={(e) => setDriverUserId(e.target.value)} placeholder="Driver User ID (optional)" />
          </div>

          <div className="flex flex-wrap gap-2">
            <button onClick={() => void saveForPlanning()} disabled={saving} className="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-800 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save for Planning'}
            </button>
            <button onClick={() => void postAsLoad()} disabled={posting || !canPostAsLoad} className="bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50">
              {posting ? 'Posting...' : 'Post as Load'}
            </button>
            <button onClick={() => void assignDirect()} disabled={assigning || !canAssignDirect} className="bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-emerald-800 disabled:opacity-50">
              {assigning ? 'Assigning...' : 'Assign Directly'}
            </button>
            {order.postedLoadId && (
              <Link to={`/loads/${order.postedLoadId}`} className="px-4 py-2 rounded-lg border text-sm text-blue-700 hover:underline">
                Open Load
              </Link>
            )}
          </div>
        </fieldset>
      </div>
    </div>
  )
}
